import React from "react";
import { styled } from "@mui/material";
import { I_GENERIC_ITEMS } from "@/src-refactor/modules/shared/model/interface/items.interface";

interface DropdownContentProps {
  isOpen: boolean;
  dropdownRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  placeholderSearch: string;
  searchText: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: I_GENERIC_ITEMS[];
  filteredItems: I_GENERIC_ITEMS[];
  hiddenSearch?: boolean;
  hiddenChips?: boolean;
  hiddenSelections?: boolean;
  renderOptionItem: (
    item: I_GENERIC_ITEMS,
    isSelected: boolean,
    index: number
  ) => React.ReactNode;
  id?: string;
  maxHeight?: number;
}

const DropdownContainer = styled("div")<{ isOpen: boolean; maxHeight?: number }>(
  ({ isOpen, maxHeight }) => ({
    position: "absolute",
    top: "100%",
    left: 0,
    width: "100%",
    minWidth: "120px",
    background: "white",
    borderRadius: "3px",
    boxShadow: "0 4px 8px rgba(9, 30, 66, 0.25)",
    marginTop: "4px",
    display: isOpen ? "flex" : "none",
    flexDirection: "column",
    zIndex: 10000,
    isolation: "isolate",
    overflow: maxHeight ? "hidden" : "visible",
    ...(maxHeight
      ? {
          maxHeight,
          minHeight: 0
        }
      : {})
  })
);

const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  height: "40px",
  minHeight: 40,
  padding: "0 12px",
  border: "none",
  borderBottom: "1px solid #dfe1e6",
  fontSize: "14px",
  outline: "none",
  "&::placeholder": {
    color: theme.palette.grey[600]
  },
  cursor: "text"
}));

const OptionsList = styled("div")<{ hasMaxHeight: boolean }>(({ hasMaxHeight }) => ({
  overflowY: "auto",
  ...(hasMaxHeight
    ? {
        flex: 1,
        minHeight: 0
      }
    : {
        maxHeight: "400px"
      })
}));

const SelectedSection = styled("div")({
  borderBottom: "1px solid #dfe1e6",
  padding: "8px 0"
});

const SelectedTitle = styled("div")({
  padding: "4px 12px",
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b778c",
  textTransform: "uppercase",
  letterSpacing: "0.5px"
});

const DropdownContent: React.FC<DropdownContentProps> = ({
  isOpen,
  dropdownRef,
  searchInputRef,
  placeholderSearch,
  searchText,
  onSearchChange,
  value,
  filteredItems,
  renderOptionItem,
  id = "",
  hiddenSearch = false,
  hiddenChips = false,
  hiddenSelections = false,
  maxHeight
}) => {
  return (
    <DropdownContainer isOpen={isOpen} ref={dropdownRef} maxHeight={maxHeight}>
      {!hiddenSearch && (
        <SearchInput
          ref={searchInputRef}
          placeholder={placeholderSearch}
          value={searchText}
          onChange={onSearchChange}
          autoFocus={true}
          id={`${id}-search`}
          data-testid={`${id}-search`}
        />
      )}
      <OptionsList
        hasMaxHeight={Boolean(maxHeight)}
        id={`${id}-options-list`}
        data-testid={`${id}-options-list`}
        role="listbox"
      >
        {!hiddenSelections && value.length > 0 && (
          <SelectedSection
            id={`${id}-selected-section`}
            data-testid={`${id}-selected-section`}
          >
            <SelectedTitle>Seleccionados</SelectedTitle>
            {value.map((item, index) => renderOptionItem(item, true, index))}
          </SelectedSection>
        )}
        {filteredItems.map((item, index) => renderOptionItem(item, false, index))}
      </OptionsList>
    </DropdownContainer>
  );
};

export default React.memo(DropdownContent);
